import { useEffect, useState } from "react";
import { RefreshCw, Search, SlidersHorizontal, UserRound } from "lucide-react";
import { Button, Checkbox, EmptyState, Input, Select, Tags } from "../ui";
import { categoryLabel } from "../copilot/copilotText";
import { DEFAULT_FILTERS, hasActiveFilters } from "./useCopilotInbox";
import { StatusTag, formatListStamp } from "./parts";

const activeCount = (filters) => ["category", "assignedTo", "from", "to"].filter((key) => filters[key]).length + (filters.unread ? 1 : 0) + (filters.status !== DEFAULT_FILTERS.status ? 1 : 0);

export default function InboxList({ inbox, lang, selectedId, onSelect, canAssign }) {
  const { t, filters, list, categories } = inbox;
  const [open, setOpen] = useState(false);
  const count = activeCount(filters);

  // Agents are only needed (and only allowed) for the "assigned to…" filter.
  useEffect(() => {
    if (open && canAssign && inbox.agents.status === "idle") inbox.loadAgents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canAssign]);

  const set = (key) => (event) => inbox.updateFilters({ [key]: event.target.type === "checkbox" ? event.target.checked : event.target.value });
  const assignedValue = filters.assignedTo;
  const statusOptions = [
    { value: "", label: t.allStatuses },
    { value: "unresolved", label: t.unresolvedStatuses },
    { value: "open", label: t.statusNames.open },
    { value: "pending", label: t.statusNames.pending },
    { value: "resolved", label: t.statusNames.resolved },
  ];
  const categoryOptions = [{ value: "", label: t.allCategories }, ...(categories || []).map((cat) => ({ value: cat.value, label: lang === "ar" ? cat.labelAr : cat.labelEn }))];
  const assignOptions = [
    { value: "", label: t.anyone },
    { value: "me", label: t.mine },
    { value: "unassigned", label: t.unassignedOption },
    ...inbox.agents.items.map((agent) => ({ value: agent.id, label: `${t.byAgent} ${agent.name || agent.id}` })),
  ];

  return (
    <div className="ca-list">
      <div className="ca-list-head">
        <Input
          type="search"
          className="ca-search"
          iconStart={<Search size={16} />}
          value={inbox.searchText}
          onChange={(event) => inbox.setSearchText(event.target.value)}
          placeholder={t.search}
          aria-label={t.search}
          maxLength={100}
        />
        <Button variant={count ? "primary" : "neutral"} appearance={count ? "soft" : "ghost"} className="ca-filter-toggle" aria-expanded={open} aria-controls="ca-filters" onClick={() => setOpen((v) => !v)} title={t.filters} icon={<SlidersHorizontal size={18} aria-hidden="true" />}>
          <span className="ca-sr">{t.filters}</span>
          {count > 0 && <span className="ca-filter-count" aria-hidden="true">{count}</span>}
        </Button>
        <Button variant="neutral" appearance="ghost" onClick={() => inbox.loadList({ silent: false })} aria-label={t.refresh} title={t.refresh} icon={<RefreshCw size={16} aria-hidden="true" />} />
      </div>

      {open && (
        <div id="ca-filters" className="ca-filters" role="group" aria-label={t.filters}>
          <Select label={t.status} options={statusOptions} value={filters.status} onChange={set("status")} />
          <Select label={t.category} options={categoryOptions} value={filters.category} onChange={set("category")} />
          <Select label={t.assignment} options={assignOptions} value={assignedValue} onChange={set("assignedTo")} />
          <div className="ca-field-row">
            <Input type="date" label={t.from} value={filters.from} max={filters.to || undefined} onChange={set("from")} />
            <Input type="date" label={t.to} value={filters.to} min={filters.from || undefined} onChange={set("to")} />
          </div>
          <Checkbox label={t.onlyUnread} checked={filters.unread} onChange={set("unread")} />
          {hasActiveFilters(filters) && (
            <Button variant="neutral" appearance="ghost" size="sm" onClick={inbox.clearFilters}>{t.clearFilters}</Button>
          )}
        </div>
      )}

      <div className="ca-list-scroll" aria-busy={list.status === "loading"}>
        {list.status === "loading" && (
          <div className="ca-skeletons" role="status" aria-label={t.loading}>
            {[0, 1, 2, 3, 4].map((i) => <div className="ca-skeleton" key={i} />)}
          </div>
        )}
        {list.status === "error" && (
          <EmptyState variant="error" action={<Button variant="neutral" appearance="outline" size="sm" onClick={() => inbox.loadList({ silent: false })}>{t.retry}</Button>}>
            {list.error || t.listError}
          </EmptyState>
        )}
        {list.status === "ready" && list.items.length === 0 && (
          <EmptyState title={t.empty} action={hasActiveFilters(filters) ? <Button variant="neutral" appearance="outline" size="sm" onClick={inbox.clearFilters}>{t.clearFilters}</Button> : null}>
            {hasActiveFilters(filters) ? t.emptyFiltered : t.emptyAll}
          </EmptyState>
        )}
        {list.items.length > 0 && (
          <ul className="ca-items">
            {list.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`ca-item ${item.id === selectedId ? "selected" : ""} ${item.unreadCount ? "unread" : ""}`}
                  aria-current={item.id === selectedId ? "true" : undefined}
                  onClick={() => onSelect(item.id)}
                >
                  <span className="ca-item-top">
                    <span className="ca-item-subject">{item.subject}</span>
                    <span className="ca-item-time">{formatListStamp(item.lastActivityAt, lang)}</span>
                  </span>
                  <span className="ca-item-customer">
                    {item.customer.name}
                    {item.customer.kind === "guest" && <Tags size="sm" variant="warning">{t.guest}</Tags>}
                  </span>
                  <span className="ca-item-preview">{item.lastMessage?.text || ""}</span>
                  <span className="ca-item-meta">
                    <StatusTag status={item.status} t={t} />
                    <span className="ca-item-category">{categoryLabel(categories, item.category, lang)}</span>
                    <span className="ca-item-assignee" title={t.assignedTo}>
                      <UserRound size={12} aria-hidden="true" />
                      {item.assignedTo?.name || (item.assignedTo ? t.assignedAgentBadge : t.unassignedLabel)}
                    </span>
                    {item.unreadCount > 0 && (
                      <span className="ca-unread" role="img" aria-label={t.unreadLabel(item.unreadCount)}>{item.unreadCount > 9 ? "9+" : item.unreadCount}</span>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {list.hasMore && (
          <div className="ca-more">
            <Button variant="neutral" appearance="outline" size="sm" loading={list.loadingMore} onClick={inbox.loadMore}>{t.loadMore}</Button>
          </div>
        )}
        {list.status === "ready" && list.error && <p className="ca-error" role="alert">{list.error}</p>}
      </div>
    </div>
  );
}
